/**
 * CompletenessMeter.js — RC 1.29.D.
 *
 * Tiny summary card that sits at the top of the Settings tab. Shows
 * two bars: Profile completeness and Preferences completeness, plus
 * a short list of missing items for whichever side is incomplete.
 *
 * Derives completeness from data that the Profile + Preferences
 * cards on the same page already load — we just re-fetch the same
 * two endpoints at mount time, no new backend surface:
 *
 *   GET /api/profile/me           (RC 1.29)
 *   GET /api/profile/preferences  (RC 1.27)
 *
 * To stay in sync when the user saves, the meter listens for a
 * lightweight window event `ndor:profile-refresh`. The Profile
 * editor and the Preferences card each dispatch this event on
 * successful save — so the bars update without any prop-drilling
 * or shared store.
 *
 * Pure presentation over existing data. No scoring, automation,
 * NDOR, or ATS logic is touched.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api';

// ── Field inventory ──────────────────────────────────────────
// Exactly what the slice spec asks us to count.
const PROFILE_FIELDS = [
  { key: 'full_name',         label: 'Full name' },
  { key: 'headline',          label: 'Headline' },
  { key: 'current_job_title', label: 'Current job title' },
  { key: 'current_company',   label: 'Current company' },
  { key: 'years_experience',  label: 'Years of experience' },
  { key: 'location',          label: 'Location' },
  { key: 'visa_status',       label: 'Visa / work authorisation' },
  { key: 'summary',           label: 'Experience summary' },
  { key: 'primary_domain',    label: 'Primary domain' },
  { key: 'skills_json',       label: 'Skills (at least one)' },
  { key: 'primary_cv',        label: 'Primary Source CV' },
];

const PREFERENCE_FIELDS = [
  { key: 'preferred_regions',    label: 'Preferred regions (at least one)' },
  { key: 'open_to_relocation',   label: 'Open to relocation' },
  { key: 'relocation_regions',   label: 'Relocation regions' },
  { key: 'location_strictness',  label: 'Location strictness' },
  { key: 'include_cover_letter', label: 'Include cover letter choice' },
];

// RC 1.29.D.1 — short, rule-based "why fill this" hints. Pure
// frontend, no logic. Rendered under the "Missing:" line when at
// least one missing field has an entry here. Kept terse on
// purpose — no AI tone, no persuasion copy.
const OUTCOME_HINTS = {
  skills_json:         'Improves job match scoring',
  primary_domain:      'Helps role relevance detection',
  headline:            'Improves profile clarity in applications',
  summary:             'Strengthens repositioning advice',
  preferred_regions:   'Improves location match accuracy',
  location_strictness: 'Controls how strict job filtering feels',
};

function pickHint(missing) {
  // Pick the first hint in the spec-defined priority order that
  // matches a currently-missing field. One hint at a time keeps the
  // UI quiet.
  const priority = [
    'skills_json', 'primary_domain', 'headline', 'summary',
    'preferred_regions', 'location_strictness',
  ];
  const keys = new Set(missing.map((m) => m.key));
  for (const k of priority) if (keys.has(k)) return OUTCOME_HINTS[k];
  return null;
}

// ── Per-field completeness rules ─────────────────────────────
function isProfileFieldFilled(field, profile, primaryCv) {
  switch (field.key) {
    case 'years_experience':
      return typeof profile?.years_experience === 'number'
        && profile.years_experience >= 0;
    case 'skills_json':
      return Array.isArray(profile?.skills_json) && profile.skills_json.length > 0;
    case 'primary_cv':
      return Boolean(primaryCv?.id);
    default: {
      const v = profile?.[field.key];
      return typeof v === 'string' ? v.trim().length > 0 : Boolean(v);
    }
  }
}

function isPreferenceFieldFilled(field, prefs) {
  switch (field.key) {
    case 'preferred_regions':
      return Array.isArray(prefs?.preferred_regions) && prefs.preferred_regions.length > 0;
    case 'relocation_regions':
      // If the user isn't open to relocation this question is N/A
      // — counted as "filled" because there is nothing to fill.
      if (prefs?.open_to_relocation === 'no') return true;
      return Array.isArray(prefs?.relocation_regions) && prefs.relocation_regions.length > 0;
    case 'open_to_relocation':
      return ['yes', 'conditional', 'no'].includes(prefs?.open_to_relocation);
    case 'location_strictness':
      return ['strict', 'balanced', 'open'].includes(prefs?.location_strictness);
    case 'include_cover_letter':
      return typeof prefs?.include_cover_letter === 'boolean';
    default:
      return false;
  }
}

function computeCompleteness(profile, primaryCv, prefs) {
  const profFilled = PROFILE_FIELDS.map((f) => ({
    ...f, filled: isProfileFieldFilled(f, profile, primaryCv),
  }));
  const prefFilled = PREFERENCE_FIELDS.map((f) => ({
    ...f, filled: isPreferenceFieldFilled(f, prefs),
  }));
  const profDone = profFilled.filter((x) => x.filled).length;
  const prefDone = prefFilled.filter((x) => x.filled).length;
  return {
    profile_pct:  Math.round((profDone / PROFILE_FIELDS.length) * 100),
    preference_pct: Math.round((prefDone / PREFERENCE_FIELDS.length) * 100),
    missing_profile:    profFilled.filter((x) => !x.filled),
    missing_preference: prefFilled.filter((x) => !x.filled),
  };
}

// ── UI atoms ─────────────────────────────────────────────────
function Bar({ pct, testId }) {
  // tone shifts as the bar fills, deliberately muted
  const tone = pct >= 100
    ? 'bg-ndor-success'
    : pct >= 60
      ? 'bg-ndor-gold'
      : 'bg-ndor-warning';
  return (
    <div
      className="h-1.5 w-full rounded-full bg-ndor-surface-alt overflow-hidden"
      data-testid={testId}
    >
      <div
        className={`h-full ${tone} transition-all`}
        style={{ width: `${Math.max(3, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

function Section({ title, pct, missing, barTestId, missingTestId, targetId }) {
  const hint = pickHint(missing);
  const scroll = () => {
    if (!targetId) return;
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div className="flex-1 min-w-0 space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">
          {title}
        </span>
        <span
          className="text-sm font-medium text-ndor-ink font-mono-ui"
          data-testid={`${barTestId}-pct`}
        >
          {pct}%
        </span>
      </div>
      <button
        type="button"
        onClick={scroll}
        aria-label={`Scroll to ${title}`}
        className="block w-full text-left"
        data-testid={`${barTestId}-click`}
      >
        <Bar pct={pct} testId={barTestId} />
      </button>
      {missing.length > 0 ? (
        <div className="pt-0.5 space-y-0.5">
          <p
            className="text-[11px] text-ndor-ink-muted"
            data-testid={missingTestId}
          >
            <AlertCircle className="h-3 w-3 inline mr-1 -mt-0.5 text-ndor-warning" />
            Missing:{' '}
            <span className="text-ndor-ink-secondary">
              {missing.slice(0, 3).map((m) => m.label).join(' · ')}
            </span>
            {missing.length > 3 && (
              <span className="text-ndor-ink-muted">
                {' '}+{missing.length - 3} more
              </span>
            )}
          </p>
          {hint && (
            <p
              className="text-[11px] text-ndor-gold pl-[18px]"
              data-testid={`${missingTestId}-hint`}
            >
              → {hint}
            </p>
          )}
        </div>
      ) : (
        <p className="text-[11px] text-ndor-success pt-0.5 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Complete
        </p>
      )}
    </div>
  );
}

export default function CompletenessMeter() {
  const [profile, setProfile] = useState(null);
  const [primaryCv, setCv]    = useState(null);
  const [prefs, setPrefs]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [me, p] = await Promise.all([
        apiFetch('/api/profile/me').catch(() => null),
        apiFetch('/api/profile/preferences').catch(() => null),
      ]);
      setProfile(me?.profile || null);
      setCv(me?.primary_cv || null);
      setPrefs(p?.preferences || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh whenever a card on the same page saves.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('ndor:profile-refresh', handler);
    return () => window.removeEventListener('ndor:profile-refresh', handler);
  }, [load]);

  const stats = useMemo(
    () => (loading || !profile || !prefs
      ? null
      : computeCompleteness(profile, primaryCv, prefs)),
    [loading, profile, primaryCv, prefs],
  );

  return (
    <Card
      className="border-ndor-edge shadow-sm"
      data-testid="completeness-meter-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
          Profile completeness
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading || !stats ? (
          <p className="text-xs text-ndor-ink-muted">Loading…</p>
        ) : (
          <div className="flex flex-col sm:flex-row gap-5">
            <Section
              title="Profile"
              pct={stats.profile_pct}
              missing={stats.missing_profile}
              barTestId="completeness-profile-bar"
              missingTestId="completeness-profile-missing"
              targetId="profile-section"
            />
            <Section
              title="Preferences"
              pct={stats.preference_pct}
              missing={stats.missing_preference}
              barTestId="completeness-preferences-bar"
              missingTestId="completeness-preferences-missing"
              targetId="preferences-section"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { computeCompleteness, PROFILE_FIELDS, PREFERENCE_FIELDS };
